import { useState } from "react";
import {
  CheckCircleIcon,
  CircleIcon,
  PlusIcon,
  TrashIcon,
} from "@phosphor-icons/react";

import api, { formatApiError } from "../../lib/api";
import {
  ErrorBanner,
  PrimaryButton,
  SecondaryButton,
  TextInput,
} from "../crm/Form";

export default function ChecklistEditor({
  workOrderId,
  items,
  canManage,
  canToggle,
  onChange,
}) {
  const [adding, setAdding] = useState(false);
  const [label, setLabel] = useState("");
  const [required, setRequired] = useState(false);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");

  const toggle = async (item) => {
    setErr("");
    try {
      await api.patch(
        `/work-orders/${workOrderId}/checklist/${item.id}`,
        { is_completed: !item.is_completed }
      );
      onChange();
    } catch (e) {
      setErr(formatApiError(e));
    }
  };

  const add = async (e) => {
    e.preventDefault();
    setErr("");
    setBusy(true);
    try {
      await api.post(`/work-orders/${workOrderId}/checklist`, {
        label,
        is_required: required,
        sort_order: items.length,
      });
      setLabel("");
      setRequired(false);
      setAdding(false);
      onChange();
    } catch (e2) {
      setErr(formatApiError(e2));
    } finally {
      setBusy(false);
    }
  };

  const remove = async (item) => {
    setErr("");
    try {
      await api.delete(`/work-orders/${workOrderId}/checklist/${item.id}`);
      onChange();
    } catch (e) {
      setErr(formatApiError(e));
    }
  };

  return (
    <div data-testid="checklist-editor" className="space-y-1">
      <ErrorBanner message={err} />
      {items.length === 0 && !adding && (
        <div className="text-xs text-slate-500 py-2">
          No checklist items yet.
        </div>
      )}
      <ul className="divide-y divide-slate-100">
        {items.map((it) => (
          <li
            key={it.id}
            className="flex items-start gap-3 py-2"
            data-testid={`checklist-item-${it.id}`}
          >
            <button
              type="button"
              data-testid={`checklist-toggle-${it.id}`}
              disabled={!canToggle}
              onClick={() => toggle(it)}
              className="mt-0.5 disabled:opacity-50 disabled:cursor-not-allowed"
              aria-label={it.is_completed ? "Mark incomplete" : "Mark complete"}
            >
              {it.is_completed ? (
                <CheckCircleIcon
                  size={20}
                  weight="fill"
                  className="text-emerald-600"
                />
              ) : (
                <CircleIcon
                  size={20}
                  weight="regular"
                  className="text-slate-400 hover:text-slate-700"
                />
              )}
            </button>
            <div className="flex-1 min-w-0">
              <div
                className={`text-sm ${
                  it.is_completed ? "line-through text-slate-500" : "text-slate-900"
                }`}
              >
                {it.label}
                {it.is_required && (
                  <span className="ml-2 font-data text-[9px] uppercase tracking-widest text-amber-700">
                    required
                  </span>
                )}
              </div>
              {it.is_completed && it.completed_at && (
                <div className="font-data text-[10px] text-slate-500 mt-0.5">
                  done {new Date(it.completed_at).toLocaleString()}
                </div>
              )}
            </div>
            {canManage && (
              <button
                data-testid={`checklist-delete-${it.id}`}
                onClick={() => remove(it)}
                type="button"
                className="text-rose-600 hover:text-rose-800"
                aria-label="Remove item"
              >
                <TrashIcon size={14} />
              </button>
            )}
          </li>
        ))}
      </ul>

      {canManage && (
        <div className="pt-2">
          {adding ? (
            <form onSubmit={add} className="flex flex-wrap items-end gap-2">
              <div className="flex-1 min-w-[180px]">
                <TextInput
                  value={label}
                  onChange={setLabel}
                  required
                  maxLength={300}
                  data-testid="checklist-new-label"
                  placeholder="Item description…"
                />
              </div>
              <label className="flex items-center gap-1.5 text-[10px] uppercase tracking-[0.18em] text-slate-600 font-data pb-2">
                <input
                  type="checkbox"
                  checked={required}
                  onChange={(e) => setRequired(e.target.checked)}
                  data-testid="checklist-new-required"
                />
                required
              </label>
              <PrimaryButton
                type="submit"
                disabled={busy}
                data-testid="checklist-new-submit"
              >
                {busy ? "Adding…" : "Add"}
              </PrimaryButton>
              <SecondaryButton
                type="button"
                onClick={() => {
                  setAdding(false);
                  setLabel("");
                }}
              >
                Cancel
              </SecondaryButton>
            </form>
          ) : (
            <button
              type="button"
              data-testid="checklist-add-button"
              onClick={() => setAdding(true)}
              className="inline-flex items-center gap-2 font-data text-[10px] uppercase tracking-[0.18em] text-slate-700 hover:text-amber-700"
            >
              <PlusIcon size={12} weight="bold" /> Add Item
            </button>
          )}
        </div>
      )}
    </div>
  );
}
